import { eventTarget, getRenderingEngine } from "@cornerstonejs/core";
import * as cornerstoneTools from "@cornerstonejs/tools";
import { computeLabelmapData } from "@cornerstonejs/polymorphic-segmentation";

const { Enums: csToolsEnums } = cornerstoneTools;

/**
 * Sets up event listener to automatically convert contour annotations to labelmap
 * when a spline contour is completed
 * @param toolGroupId The ID of the tool group
 * @returns Cleanup function to remove the event listener
 */
export const setupContourToLabelmapConverter = (
  toolGroupId: string
): (() => void) => {
  const labelmapSegmentationId = "DRAFT_SEGMENTATION";
  const contourSegmentationId = "DRAFT_CONTOUR_SEGMENTATION";

  const handleAnnotationCompleted = async (evt: any) => {
    const { annotation } = evt.detail;

    try {
      // Get the viewport from the annotation or event
      const renderingEngine = getRenderingEngine("myRenderingEngine");
      if (!renderingEngine) {
        console.error("Rendering engine not found");
        return;
      }

      // Try to get viewport from the annotation metadata or use the first available viewport
      let viewport = null;
      if (annotation?.metadata?.viewportId) {
        viewport = renderingEngine.getViewport(annotation.metadata.viewportId);
      }

      // If no viewport from annotation, try to get from event detail
      if (!viewport && evt.detail.viewportId) {
        viewport = renderingEngine.getViewport(evt.detail.viewportId);
      }

      // Fallback to axial viewport
      if (!viewport) {
        viewport = renderingEngine.getViewport("CT_AXIAL");
      }

      if (!viewport) {
        console.error("Could not find viewport for contour conversion");
        return;
      }

      // Get the active segment index from the contour segmentation
      const activeSegmentIndex =
        cornerstoneTools.segmentation.segmentIndex.getActiveSegmentIndex(
          contourSegmentationId
        );

      // Convert the contour segmentation to labelmap using polymorphic segmentation
      // This will compute labelmap data from all contours in the contour segmentation
      const result = await computeLabelmapData(contourSegmentationId, {
        segmentIndices: [activeSegmentIndex],
        viewport: viewport,
      });

      // The result contains just the volumeId, we need to fetch the volume from cache
      if (
        result &&
        "volumeId" in result &&
        typeof result.volumeId === "string"
      ) {
        const { cache } = await import("@cornerstonejs/core");
        const contourLabelmap = cache.getVolume(result.volumeId);

        if (!contourLabelmap) {
          console.error(
            "Could not retrieve contour labelmap volume from cache:",
            result.volumeId
          );
          return;
        }

        // Get scalar data from the contour labelmap volume
        // Try different possible locations for the data
        let contourScalarData = (contourLabelmap as any).scalarData;

        if (!contourScalarData && (contourLabelmap as any).voxelManager) {
          contourScalarData = (
            contourLabelmap as any
          ).voxelManager.getCompleteScalarDataArray?.();
        }

        if (!contourScalarData && (contourLabelmap as any).imageData) {
          contourScalarData = (contourLabelmap as any).imageData
            .getPointData?.()
            ?.getScalars()
            ?.getData();
        }

        if (!contourScalarData) {
          console.error("No scalarData in contour labelmap volume");
          return;
        }

        // Get the main labelmap segmentation
        const mainLabelmapSegmentation =
          cornerstoneTools.segmentation.state.getSegmentation(
            labelmapSegmentationId
          );

        if (
          mainLabelmapSegmentation &&
          mainLabelmapSegmentation.representationData.Labelmap
        ) {
          const mainLabelmapData =
            mainLabelmapSegmentation.representationData.Labelmap;

          // Get the volume ID from main labelmap
          let mainVolumeId: string | undefined;
          if (
            "volumeId" in mainLabelmapData &&
            typeof mainLabelmapData.volumeId === "string"
          ) {
            mainVolumeId = mainLabelmapData.volumeId;
          }

          if (mainVolumeId) {
            const mainVolume = cache.getVolume(mainVolumeId);

            if (mainVolume) {
              // Get scalar data from main volume using same approach as contour volume
              let mainScalarData = (mainVolume as any).scalarData;

              if (!mainScalarData && (mainVolume as any).voxelManager) {
                mainScalarData = (
                  mainVolume as any
                ).voxelManager.getCompleteScalarDataArray?.();
              }

              if (!mainScalarData && (mainVolume as any).imageData) {
                mainScalarData = (mainVolume as any).imageData
                  .getPointData?.()
                  ?.getScalars()
                  ?.getData();
              }

              if (!mainScalarData) {
                console.error("No scalarData in main volume");
                return;
              }

              // When using voxelManager, we need to use its API to modify data
              const mainVoxelManager = (mainVolume as any).voxelManager;
              const contourVoxelManager = (contourLabelmap as any).voxelManager;

              const [srcW, srcH, srcD] = contourLabelmap.dimensions;
              const [dstW, dstH, dstD] = mainVolume.dimensions;

              if (srcW === dstW && srcH === dstH && srcD === dstD) {
                // Same dimensions — direct copy
                for (let i = 0; i < contourScalarData.length; i++) {
                  if (contourScalarData[i] !== 0) {
                    mainVoxelManager.setAtIndex(i, activeSegmentIndex);
                  }
                }
              } else {
                // Different dimensions (supersampled labelmap) — nearest-neighbor upsample
                const scaleX = srcW / dstW;
                const scaleY = srcH / dstH;
                const scaleZ = srcD / dstD;
                for (let z = 0; z < dstD; z++) {
                  const sZ = Math.min(Math.floor(z * scaleZ), srcD - 1);
                  for (let y = 0; y < dstH; y++) {
                    const sY = Math.min(Math.floor(y * scaleY), srcH - 1);
                    for (let x = 0; x < dstW; x++) {
                      const sX = Math.min(Math.floor(x * scaleX), srcW - 1);
                      const srcIdx = sZ * srcW * srcH + sY * srcW + sX;
                      if (contourScalarData[srcIdx] !== 0) {
                        const dstIdx = z * dstW * dstH + y * dstW + x;
                        mainVoxelManager.setAtIndex(dstIdx, activeSegmentIndex);
                      }
                    }
                  }
                }
              }

              // Trigger segmentation modified event
              cornerstoneTools.segmentation.triggerSegmentationEvents.triggerSegmentationDataModified(
                labelmapSegmentationId
              );
            }
          } else {
            console.warn("Could not find volume ID for main labelmap");
          }
        }
      }

      // Trigger a re-render to show the updated labelmap
      if (renderingEngine) {
        renderingEngine.render();
      }

      // Remove the contour annotation after converting to labelmap
      // This prevents duplicate visualization
      if (annotation && annotation.annotationUID) {
        cornerstoneTools.annotation.state.removeAnnotation(
          annotation.annotationUID
        );
      }
    } catch (error) {
      console.error("Failed to convert contour to labelmap:", error);
    }
  };

  // Listen for annotation completed events
  eventTarget.addEventListener(
    csToolsEnums.Events.ANNOTATION_COMPLETED,
    handleAnnotationCompleted
  );

  // Return cleanup function
  return () => {
    eventTarget.removeEventListener(
      csToolsEnums.Events.ANNOTATION_COMPLETED,
      handleAnnotationCompleted
    );
  };
};
